import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import AppHeader from '../components/AppHeader';
import { Trash2, FileText, Clock, AlertCircle } from 'lucide-react';

export default function History() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user) { nav('/auth'); return; }
    api.get('/assessments').then(r => setItems(r.data)).finally(() => setLoading(false));
  }, [user, nav]);

  const openAssessment = async (assessment) => {
    setError('');
    if (assessment.status === 'generated') { nav(`/reports/${assessment.id}`); return; }
    try {
      const source = assessment.regeneration_source_id
        ? (await api.get(`/assessments/${assessment.regeneration_source_id}`)).data : null;
      nav('/assessment', { state: { assessment, regeneration_source: source } });
    } catch { setError('This draft could not be opened. Please try again.'); }
  };

  const clearAll = async () => {
    if (!window.confirm('Clear all entries from History? Payment records and regeneration counts will be retained.')) return;
    await api.delete('/assessments');
    setItems([]);
  };

  return (
    <div className="min-h-screen bg-white">
      <AppHeader />
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-3 font-mono">/ Account · History</div>
        <div className="flex justify-between items-end mb-8 pb-6 border-b border-zinc-200">
          <h1 className="font-display text-4xl font-bold tracking-tight text-zinc-950">Assessment History</h1>
          {items.length > 0 && (
            <button onClick={clearAll} className="text-xs text-zinc-600 hover:text-red-500 flex items-center gap-1.5" data-testid="clear-history-btn">
              <Trash2 className="w-3.5 h-3.5" /> Clear all
            </button>
          )}
        </div>

        {error && <div role="alert" className="text-red-700 mb-4">{error}</div>}
        {loading && <div className="text-zinc-500 text-sm">Loading…</div>}

        {!loading && items.length === 0 && (
          <div className="border border-dashed border-zinc-300 p-16 text-center" data-testid="empty-history">
            <AlertCircle className="w-10 h-10 text-zinc-300 mx-auto mb-4" strokeWidth={1.2} />
            <div className="font-display text-lg text-zinc-700">No assessments yet</div>
            <p className="text-sm text-zinc-500 mt-2">Saved drafts and paid, completed reports will appear here.</p>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-px bg-zinc-200">
          {items.map(it => (
            <button
              key={it.id}
              onClick={() => openAssessment(it)}
              className="bg-white p-6 hover:bg-zinc-50 transition-colors text-left"
              data-testid={`history-item-${it.id}`}
            >
              <div className="flex items-center gap-2 mb-3">
                <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 ${it.status === 'generated' ? 'bg-green-100 text-green-700' : 'bg-zinc-100 text-zinc-600'}`}>
                  {{ draft: 'Draft', synopsis_generated: 'Synopsis ready', generated: 'Report ready' }[it.status] || it.status}
                </span>
                <span className="text-[11px] text-zinc-500 flex items-center gap-1">
                  <Clock className="w-3 h-3" /> {new Date(it.created_at || it.timestamp).toLocaleString()}
                </span>
              </div>
              <div className="font-display font-semibold text-zinc-950">
                {it.form?.company_information?.['Company Name'] || 'Untitled Assessment'}
              </div>
              <div className="text-xs text-zinc-500 mt-1">
                {(it.form?.goals || []).length} goal(s) · {(it.form?.data_sources || []).length} data source(s)
              </div>
              {it.reports?.readiness_level && (
                <div className="mt-3 text-sm flex items-center gap-2">
                  <FileText className="w-4 h-4 text-[#002FA7]" />
                  <span className="text-zinc-900">{it.reports.readiness_level}</span>
                </div>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
